// ===========================================
// Late Fees card (Invoice settings) — #372 plan A6.1
// ===========================================
// Every amount, day and count is a per-school setting (MNM's values are the defaults). The
// server is the authority on every rule and on what a save DOES (the start date, stricter or
// not); these checks only save a round trip. Shown before late fees are on, so a school can set
// them up first.

import { Controller, useWatch, type Control } from 'react-hook-form';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { InvoiceSettingsFormData } from './InvoiceSettingsTab';
import { dayKeyToDisplay } from '../../../utils/describeLateFeeChange';
import type { LastChanceWarning } from '../../../types/adminSettings.types';
import LastChanceNotices from './LastChanceNotices';

interface LateFeesCardProps {
  control: Control<InvoiceSettingsFormData>;
  /** May late fees be switched on at all (LATE_FEES_ENABLED on the server)? */
  lateFeesActive: boolean;
  /** Are they on NOW (the saved, effective state)? */
  savedEnabled: boolean;
  /** The saved start date (YYYY-MM-DD), or null. */
  enabledFrom: string | null;
  /** "GST", "VAT" or the custom name. */
  taxName: string;
  /** #372: the server's verdict on the Last Chance step; null/absent = fine. */
  lastChanceWarning?: LastChanceWarning | null;
  /** #372: the server's message for a refused save (LAST_CHANCE_REQUIRED), shown inline. */
  saveBlockedMessage?: string | null;
}

type MoneyField = 'lateFeeAmount' | 'lateFeePercent' | 'lateFeePerFeeCap' | 'lateFeeMaxTotal' | 'lateFeeMinOwing';
type DaysField = 'lateFeeFirstDays' | 'lateFeeRepeatFirstDays' | 'lateFeeRepeatEveryDays' | 'lateFeeLateInvoiceGapDays';
type BoolField =
  | 'lateFeeRepeatEnabled'
  | 'lateFeeChargePartPaid'
  | 'lateFeeTaxable'
  | 'lateFeeChargeTuition'
  | 'lateFeeChargeProducts'
  | 'lateFeeChargeOther'
  | 'lateFeeCopyBillingContact';

/** A dollars / percent box. Blank means "none" where the field allows it. */
function MoneyBox({
  control,
  name,
  label,
  adornment,
  helper,
  required,
  max,
}: {
  control: Control<InvoiceSettingsFormData>;
  name: MoneyField;
  label: string;
  adornment: '$' | '%';
  helper?: string;
  /** Required while late fees are on. */
  required?: boolean;
  max: number;
}) {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (value, form) => {
          if (value === '' || value === null) {
            return required && form.lateFeeEnabled ? `Enter ${label.toLowerCase()}` : true;
          }
          const n = Number(value);
          if (Number.isNaN(n) || n <= 0) return 'Enter more than 0';
          if (n > max) return `At most ${adornment === '$' ? '$' : ''}${max}${adornment === '%' ? '%' : ''}`;
          if (Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) return 'Use at most 2 decimal places';
          return true;
        },
        deps: ['lateFeeEnabled'],
      }}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          type="number"
          label={label}
          error={!!fieldState.error}
          helperText={fieldState.error?.message || helper}
          InputProps={
            adornment === '$'
              ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
              : { endAdornment: <InputAdornment position="end">%</InputAdornment> }
          }
          inputProps={{ min: 0, step: 0.01 }}
          sx={{ width: { xs: '100%', sm: 200 } }}
        />
      )}
    />
  );
}

function DaysBox({
  control,
  name,
  label,
  min,
  max,
  helper,
  validate,
}: {
  control: Control<InvoiceSettingsFormData>;
  name: DaysField;
  label: string;
  min: number;
  max: number;
  helper?: string;
  validate?: (value: number, form: InvoiceSettingsFormData) => true | string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        // A cleared box is '' — min/max skip it, and the save would send Number('') = 0.
        required: 'Enter a number of days',
        min: { value: min, message: `At least ${min}` },
        max: { value: max, message: `At most ${max}` },
        validate: validate ? (value, form) => validate(Number(value), form) : undefined,
      }}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          type="number"
          label={label}
          error={!!fieldState.error}
          helperText={fieldState.error?.message || helper}
          InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
          inputProps={{ min, max }}
          sx={{ width: { xs: '100%', sm: 200 } }}
          onChange={(e) => field.onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
        />
      )}
    />
  );
}

function Tick({ control, name, label }: { control: Control<InvoiceSettingsFormData>; name: BoolField; label: string }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
          label={label}
        />
      )}
    />
  );
}

export default function LateFeesCard({ control, lateFeesActive, savedEnabled, enabledFrom, taxName, lastChanceWarning, saveBlockedMessage }: LateFeesCardProps) {
  const type = useWatch({ control, name: 'lateFeeType' });
  const repeat = useWatch({ control, name: 'lateFeeRepeatEnabled' });
  const firstDays = useWatch({ control, name: 'lateFeeFirstDays' });
  const feesOn = useWatch({ control, name: 'lateFeeEnabled' });
  // The switch can always be turned OFF; ON only while the server allows it.
  const switchLocked = !lateFeesActive && !savedEnabled;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Late Fees"
        subheader="An automatic fee on invoices still unpaid after the due date"
      />
      <CardContent>
        <Stack spacing={3}>
          {!lateFeesActive && (
            <Alert severity="info">
              Late fees are not available yet. You can set them up now; they can be switched on once they are
              available.
            </Alert>
          )}

          {saveBlockedMessage && <Alert severity="error">{saveBlockedMessage}</Alert>}
          <LastChanceNotices warning={lastChanceWarning} feesOn={!!feesOn} firstDays={firstDays} />

          <Controller
            name="lateFeeEnabled"
            control={control}
            render={({ field }) => (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!field.value}
                      disabled={switchLocked && !field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      inputProps={{ 'aria-label': 'Charge late fees on or off' }}
                    />
                  }
                  label="Charge late fees"
                />
                {savedEnabled && enabledFrom && (
                  <FormHelperText>
                    On since {dayKeyToDisplay(enabledFrom)}: invoices due before then are never charged.
                  </FormHelperText>
                )}
              </Box>
            )}
          />

          <Divider />

          <Controller
            name="lateFeeType"
            control={control}
            render={({ field }) => (
              <FormControl>
                <FormLabel>The fee</FormLabel>
                <RadioGroup {...field} row>
                  <FormControlLabel value="FIXED" control={<Radio />} label="A fixed amount" />
                  <FormControlLabel value="PERCENT" control={<Radio />} label="A percentage of what is owed" />
                </RadioGroup>
              </FormControl>
            )}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {type === 'FIXED' ? (
              <MoneyBox control={control} name="lateFeeAmount" label="Amount" adornment="$" required max={1000} />
            ) : (
              <>
                <MoneyBox
                  control={control}
                  name="lateFeePercent"
                  label="Percentage"
                  adornment="%"
                  required
                  max={20}
                  helper="Of what is owed, not counting earlier fees"
                />
                <MoneyBox
                  control={control}
                  name="lateFeePerFeeCap"
                  label="Most for one fee"
                  adornment="$"
                  max={1000}
                  helper="Blank for no cap"
                />
              </>
            )}
          </Stack>

          <DaysBox
            control={control}
            name="lateFeeFirstDays"
            label="First fee"
            min={1}
            max={90}
            helper="Days after the due date"
          />

          <Box>
            <Controller
              name="lateFeeRepeatEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Keep charging while unpaid"
                />
              )}
            />
            {repeat && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                <DaysBox
                  control={control}
                  name="lateFeeRepeatFirstDays"
                  label="Second fee"
                  min={2}
                  max={180}
                  helper="Days after the due date"
                  validate={(value, form) =>
                    !form.lateFeeRepeatEnabled || value > Number(form.lateFeeFirstDays) || 'Must be after the first fee'
                  }
                />
                <DaysBox
                  control={control}
                  name="lateFeeRepeatEveryDays"
                  label="Then every"
                  min={1}
                  max={90}
                  helper="Days after the fee before"
                />
              </Stack>
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="lateFeeMaxCount"
              control={control}
              rules={{
                validate: (value) => {
                  if (value === '' || value === null) return true;
                  const n = Number(value);
                  return (Number.isInteger(n) && n >= 1 && n <= 52) || 'A whole number from 1 to 52';
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  type="number"
                  label="Most fees on one invoice"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message || 'Blank for no limit'}
                  inputProps={{ min: 1, max: 52 }}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                />
              )}
            />
            <MoneyBox
              control={control}
              name="lateFeeMaxTotal"
              label="Most in fees on one invoice"
              adornment="$"
              max={10000}
              helper="Blank for no cap. The fee that would pass it is charged what is left."
            />
            <MoneyBox
              control={control}
              name="lateFeeMinOwing"
              label="Least owing before a fee"
              adornment="$"
              max={10000}
              helper="Blank for any amount"
            />
          </Stack>

          <Box>
            <FormLabel>Which invoices</FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Charged when ANY of its lines is one of these kinds (set on each line).
            </Typography>
            <FormGroup row>
              <Tick control={control} name="lateFeeChargeTuition" label="Tuition" />
              <Tick control={control} name="lateFeeChargeProducts" label="Products" />
              <Tick control={control} name="lateFeeChargeOther" label="Other" />
            </FormGroup>
            {/* "At least one kind while on" is the server's rule (400 LATE_FEE_NO_KIND, shown as the save error). */}
          </Box>

          <FormGroup>
            <Tick control={control} name="lateFeeChargePartPaid" label="Charge part-paid invoices too" />
            <Tick control={control} name="lateFeeTaxable" label={`Charge ${taxName} on the fee`} />
            <Tick control={control} name="lateFeeCopyBillingContact" label="Send the billing contact a copy of each Late Fee email" />
          </FormGroup>

          <DaysBox
            control={control}
            name="lateFeeLateInvoiceGapDays"
            label="Wait after a late Last Chance email"
            min={1}
            max={60}
            helper={`When the Last Chance reminder went out late, the first fee waits this many days after it (normally the first fee is on day ${Number(firstDays) || 7}).`}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
